import { createContext, useState, ReactNode, useCallback } from "react";
import { useToast } from "@/components/ui/ToastProvider";

export interface DemoInfo {
  is_demo: boolean;
  expires_at: string;
  days_left: number;
}

export interface DemoContextType {
  isDemoMode: boolean;
  demoInfo: DemoInfo | null;
  setDemoInfo: (info: DemoInfo | null) => void;
  showDemoToast: (action: string) => void;
  /**
   * Jeśli aplikacja działa w trybie demo, pokazuje globalny DemoRestrictionModal
   * i zwraca true (wołający powinien wtedy przerwać akcję). Poza demo zwraca false.
   */
  blockInDemo: () => boolean;
  isRestrictionModalOpen: boolean;
  closeRestrictionModal: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const DemoContext = createContext<DemoContextType | undefined>(undefined);

const VITE_DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

export function DemoProvider({ children }: { children: ReactNode }) {
  const [demoInfo, setDemoInfo] = useState<DemoInfo | null>(null);
  const [isRestrictionModalOpen, setIsRestrictionModalOpen] = useState(false);
  const { info } = useToast();

  const isDemoMode = VITE_DEMO_MODE;

  const showDemoToast = useCallback(
    (action: string) => {
      info(`${action} - zasymulowano w trybie demo`);
    },
    [info]
  );

  const blockInDemo = useCallback(() => {
    if (!isDemoMode) return false;
    setIsRestrictionModalOpen(true);
    return true;
  }, [isDemoMode]);

  const closeRestrictionModal = useCallback(() => {
    setIsRestrictionModalOpen(false);
  }, []);

  return (
    <DemoContext.Provider
      value={{
        isDemoMode,
        demoInfo,
        setDemoInfo,
        showDemoToast,
        blockInDemo,
        isRestrictionModalOpen,
        closeRestrictionModal,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}
